// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '@shared/commons/DataCache';
import { OrderType } from './OrderType';
import { OrderTypeBaseParameters } from '@shared/commons/cache/OrderParams/order-type/OrderTypeBaseParameters';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { ProductType } from '../Instruments/ProductType';
import { InstrumentUtils } from '../Instruments/InstrumentUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { RulesSet } from '../Rules/RulesSet';
class _TradingUtils {
    getAllowedOrderTypes() {
        const allowedOrderTypes = [];
        const orderTypes = DataCache.OrderParameterContainer.OrderTypes;
        for (const key in orderTypes) {
            allowedOrderTypes.push(orderTypes[key].id());
        }
        const result = allowedOrderTypes.filter((type) => {
            if (type === OrderType.OCO && !IsAllowed.IsAnyAccountAllowOCO()) {
                return false;
            }
            if (type === OrderType.Care && !IsAllowed.IsUserCommentAllowed()) {
                return false;
            }
            if (type === OrderType.TrailingStop && !DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRAILING_STOP)) {
                return false;
            }
            return true;
        });
        return this.sortOrderTypes(result);
    }
    getAllowedOrderTypesForTrading(account, instrument) {
        const allowedOrderTypes = new Array();
        if (!account || !instrument) {
            return allowedOrderTypes;
        }
        if (DataCache.OrderParameterContainer === null) {
            return allowedOrderTypes;
        }
        const orderTypes = DataCache.OrderParameterContainer.OrderTypes;
        const supportedParamObj = new OrderTypeBaseParameters(instrument, account);
        for (const key in orderTypes) {
            const orderType = orderTypes[key];
            const id = orderType.id();
            if (!orderType.IsSupported(supportedParamObj)) {
                continue;
            }
            allowedOrderTypes.push(id);
        }
        return this.sortOrderTypes(allowedOrderTypes);
    }
    getAllowedTifs() {
        return SessionSettings.GetAllowedTifsForOrderTypes(this.getAllowedOrderTypes());
    }
    getAllowedProductTypes() {
        const allowedProductTypes = [];
        const productTypesCache = DataCache.productTypeEnabilityCache;
        for (const t in productTypesCache) {
            allowedProductTypes.push(+t);
        }
        return allowedProductTypes;
    }
    getAllowedProductTypesForTrading(account, instrument) {
        const productTypes = new Array();
        if (!account || !instrument) {
            return productTypes;
        }
        const dict = account.RiskPlan
            ? account.RiskPlan.GetRisksForInstrument(instrument.GetInteriorID()).availableProductTypes
            : InstrumentUtils.getAllowedProductTypeDict(instrument);
        if (dict.length === 0) {
            return productTypes;
        }
        if (dict.length === 1 && Number(dict[0]) === ProductType.General) {
            return productTypes;
        }
        for (const productType of dict) {
            productTypes.push(productType);
        }
        return productTypes;
    }
    getStartOfDay(date, dayOffset = 0) {
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + dayOffset);
        return date;
    }
    sortOrderTypes(orderTypes) {
        const stop = orderTypes.indexOf(OrderType.Stop);
        const limit = orderTypes.indexOf(OrderType.Limit);
        if (stop > -1 && limit > -1) {
            const temp = orderTypes[stop];
            orderTypes[stop] = orderTypes[limit];
            orderTypes[limit] = temp;
        }
        return orderTypes;
    }
}
export const TradingUtils = new _TradingUtils();
